import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { Icon } from '../Icon';
import styles from './ButtonOptions.module.css';
const POPOVER_MIN_WIDTH_PX = 193;
const POPOVER_VIEWPORT_PADDING_PX = 8;
const POPOVER_GAP_PX = 4;
const POPOVER_ROW_HEIGHT_ESTIMATE_PX = 44;
const POPOVER_VERTICAL_PADDING_ESTIMATE_PX = 16;
const DESTRUCTIVE_ICON_TINT = 'var(--status-error)';
const renderActionIcon = (action) => {
    if (!action.icon) {
        return null;
    }
    if (action.variant === 'destructive') {
        return (_jsx(Icon, { name: action.icon, "aria-hidden": true, variant: 'mask', tintColor: DESTRUCTIVE_ICON_TINT, className: styles.actionDestructiveIcon }));
    }
    return _jsx(Icon, { name: action.icon, "aria-hidden": true, height: 12, width: 12 });
};
export function getFixedPositionForTrigger(rect, actionCount) {
    const estimatedMenuHeight = Math.max(80, actionCount * POPOVER_ROW_HEIGHT_ESTIMATE_PX + POPOVER_VERTICAL_PADDING_ESTIMATE_PX);
    const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP_PX;
    const spaceAbove = rect.top - POPOVER_GAP_PX;
    const openDownward = spaceBelow >= estimatedMenuHeight || (spaceBelow >= spaceAbove && spaceBelow > 0);
    const top = openDownward
        ? rect.bottom + POPOVER_GAP_PX
        : Math.max(POPOVER_VIEWPORT_PADDING_PX, rect.top - estimatedMenuHeight - POPOVER_GAP_PX);
    let left = rect.right - POPOVER_MIN_WIDTH_PX;
    const maxLeft = window.innerWidth - POPOVER_MIN_WIDTH_PX - POPOVER_VIEWPORT_PADDING_PX;
    if (left < POPOVER_VIEWPORT_PADDING_PX) {
        left = POPOVER_VIEWPORT_PADDING_PX;
    }
    if (left > maxLeft) {
        left = Math.max(POPOVER_VIEWPORT_PADDING_PX, maxLeft);
    }
    return { top, left };
}
export const ButtonOptions = ({ children, actions, testIdIdentifier = 'ButtonOptions', className, triggerVariant = 'tertiary', forceOpen = false, }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState(null);
    const containerRef = useRef(null);
    const popoverRef = useRef(null);
    const isOpen = forceOpen || isMenuOpen;
    const setIsOpen = useCallback((next) => {
        if (forceOpen) {
            return;
        }
        setIsMenuOpen(next);
    }, [forceOpen]);
    const close = useCallback(() => {
        if (forceOpen) {
            return;
        }
        setIsMenuOpen(false);
        setPopoverPosition(null);
    }, [forceOpen]);
    useLayoutEffect(() => {
        if (!isOpen || !containerRef.current) {
            setPopoverPosition(null);
            return;
        }
        setPopoverPosition(getFixedPositionForTrigger(containerRef.current.getBoundingClientRect(), actions.length));
    }, [isOpen, actions.length]);
    useEffect(() => {
        if (!isOpen)
            return;
        const onMouseDownOutside = (event) => {
            const target = event.target;
            if (!target || containerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
                return;
            }
            close();
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                close();
        };
        document.addEventListener('mousedown', onMouseDownOutside);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onMouseDownOutside);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen, close]);
    const handleActionClick = useCallback((action) => {
        if (action.disabled)
            return;
        action.onClick();
        close();
    }, [close]);
    const portal = isOpen &&
        popoverPosition &&
        typeof document !== 'undefined' &&
        createPortal(_jsx("div", { ref: popoverRef, className: styles.popover, role: 'menu', "data-testid": `menu${testIdIdentifier}Menu`, style: { top: popoverPosition.top, left: popoverPosition.left }, children: actions.map((action, index) => (_jsxs("button", { type: 'button', role: 'menuitem', className: `${styles.action} ${action.variant === 'destructive' ? styles.actionDestructive : ''}`.trim(), onClick: () => handleActionClick(action), disabled: action.disabled, "data-testid": `button${testIdIdentifier}Action${action.testIdIdentifier ?? index}`, children: [renderActionIcon(action), action.label] }, action.testIdIdentifier ?? action.label))) }), document.body);
    return (_jsxs("div", { ref: containerRef, className: `${styles.container} ${className ?? ''}`.trim(), "data-testid": `div${testIdIdentifier}Container`, children: [_jsx(Button, { variant: triggerVariant, size: 'small', type: 'button', dataTestId: `button${testIdIdentifier}Trigger`, onClick: () => setIsOpen(o => !o), onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsOpen(o => !o);
                    }
                }, "aria-haspopup": 'true', "aria-expanded": isOpen, "aria-label": 'Op\u00E7\u00F5es', children: children }), portal] }));
};
